import { BehaviorSubject, Observable } from '@reactivex/rxjs';
import { $atom } from '@graphistry/falcor-json-graph';
import hoistStatics from 'react-recompose/hoistStatics';
import mapPropsStream from 'react-recompose/mapPropsStream';
import createEventHandler from 'react-recompose/createEventHandler';

import { findBinIndex } from './components/chart.tsx';

const SOURCE_ID = { interact: 0, timebar: 1 };

export const withBinHighlights = hoistStatics(
    mapPropsStream(propsStream => {
        const { handler: onTouchExit, stream: touchExits } = createEventHandler();
        const { handler: onBinTouchMove, stream: binTouchMoves } = createEventHandler();
        const { handler: onBinTouchStart, stream: binTouchStarts } = createEventHandler();
        const { handler: onBinTouchCancel, stream: binTouchCancels } = createEventHandler();
        const { handler: onBinWheel, stream: binWheels } = createEventHandler();
        const { handler: onBinClick, stream: binClicks } = createEventHandler();

        const binMovesOrWheels = binTouchMoves.merge(binWheels);

        const propsObs = Observable.from(propsStream)
            .publish()
            .refCount();
        const propsBs = new BehaviorSubject();
        propsObs.subscribe(propsBs);

        const falcorModels = propsObs.pluck('$falcor');

        const interactBinIds = binMovesOrWheels
            .filter(isBinHighlightEvent)
            .map(({ target: { dataset } }) => +dataset['binId'])
            .map(binId => [SOURCE_ID.interact, binId]);

        const timebarBinIds = propsObs
            .filter(props => props.timebar?.mode && props.timebar.playheadStart && props.details?.filtered?.binValues)
            .map(props => findBinIndex(props.timebar.playheadStart + 1, props.details.filtered.binValues))
            .map(binId => [SOURCE_ID.timebar, binId]);

        const binIds = interactBinIds
            .merge(timebarBinIds)
            .filter(([sourceId, _]) =>
                sourceId === (propsBs.value.timebar?.mode
                    ? SOURCE_ID.timebar
                    : SOURCE_ID.interact))
            .map(([_, binId]) => binId);

        const binExits = binMovesOrWheels
            .filter(x => x && (x.buttons === 0 && !isBinHighlightEvent(x)))
            .merge(touchExits)
            .filter(() => !propsBs.value.timebar?.mode)
            .mapTo(null);

        const highlightedBins = highlightBinElements(binIds, binExits, propsObs)
            .merge(binExits)
            .distinctUntilChanged()
            .withLatestFrom(falcorModels)
            .switchMap(doOrClearBinHighlight)
            .startWith(null);

        const clickedBins = binClicks
            .map(({ target: { dataset } }) => +dataset['binId'])
            .withLatestFrom(propsObs)
            .filter(([_, { timebar }]) => timebar?.mode)
            .do(([binId, { onUpdateTimebarPlayheadStart, binValues }]) =>
                onUpdateTimebarPlayheadStart({value: binValues[binId * 2]}))
            .startWith(null);

        return propsObs.combineLatest(
            highlightedBins,
            clickedBins,
            (props, highlightedBin) => ({
                ...props,
                highlightedBin,
                onTouchExit,
                onBinTouchMove,
                onBinTouchStart,
                onBinTouchCancel,
                onBinWheel,
                onBinClick,
            })
        );
    })
);

function isBinHighlightEvent({ buttons, target: { dataset } = {} } = {}) {
    return buttons === 0 && dataset && 'binId' in dataset;
}

function highlightBinElements(binIds, binExits, propsObs) {
    return binIds
        .takeUntil(binExits)
        .repeat()
        .withLatestFrom(propsObs, mapToHighlightArgs)
        .filter(([binId, binCount]) => binCount > 0);

    function mapToHighlightArgs(binId, { binCounts = [], binValues = [] }) {
        return [binId, binCounts[binId] || 0, binValues.slice(binId * 2, binId * 2 + 2)];
    }
}

function doOrClearBinHighlight([highlightArgs, $falcor]) {
    let highlightedBin = null;
    let obs = Observable.empty();
    if ($falcor && Array.isArray(highlightArgs)) {
        [highlightedBin] = highlightArgs;
        obs = $falcor.call('highlightBin', highlightArgs);
    } else if ($falcor) {
        obs = $falcor._clone({ _path: $falcor.getPath().slice(0, 4) }).set({
            json: {
                labels: { highlight: $atom(undefined) },
                highlight: {
                    darken: false,
                    edge: $atom([]),
                    point: $atom([])
                }
            }
        });
    }
    return obs.ignoreElements().startWith(highlightedBin);
}
